import BlockyBoldText from "components/custom-texts/blocky-bold-text";
import useWallet from "hooks/useWallet";
import { Entry } from "interfaces/entry";
import { createRef, useEffect, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import NumberFormat from "react-number-format";
import { useAppSelector } from "state/hooks";
import {
  getGroupCurrencySymbol,
  getGroupSecondaryColorOrDefault,
  getGroupState,
} from "state/slices/group-slice";
import {
  formatAsCurrency,
  formattedCurrencyToNumber,
} from "utils/formatter-utils/currency-formatter";
import styles from "components/pool-details/bet-modal/bet-modal-content/styles.module.css";
import ChangeHighlight from "react-change-highlight";
import OpenSansText from "components/custom-texts/open-sans-text";
import { calculateOdds } from "utils/oddsCalculator";
import { PoolDetailsResponse } from "interfaces/pool-details-response";
import confirmBet from "../confirm-bet";
import { getIntegratedAppState } from "state/slices/integrated-app-slice";
import { Bet } from "interfaces/bet";
import { toast } from "react-toastify";
import {
  resolveMinBetAmountForContestEntry,
  resolveMaxBetAmountForContestEntry,
} from "./bet-form-helpers";
import { getContestEntryById, getFullContestEntryDetailsById } from "state/slices/contests-slice";
import { NIL_GROUP_ID } from "utils/constants";
import { Contest, isBasicContest, picksCostForContestPick, remainingPicksForEntry } from "interfaces/leaderboard/contest";

interface Props {
  entry: Entry;
  pool: PoolDetailsResponse;
  onSuccessfulBetPlaced: (bet: Bet) => void;
  contestEntryId?: string;
  contest?: Contest;
}

const BetForm = ({
  entry,
  pool,
  onSuccessfulBetPlaced,
  contestEntryId,
  contest,
}: Props) => {
  const { formattedBalance, balance } = useWallet({ contestEntryId });
  const [betAmount, setBetAmount] = useState("");
  const fullEntryDetails = useAppSelector(state => getFullContestEntryDetailsById(state, contestEntryId));
  const bets = fullEntryDetails?.bets || [];
  const [loading, setLoading] = useState(false);
  const group = useAppSelector(getGroupState);
  const [validationError, setValidationError] = useState<string>();
  const [apiError, setApiError] = useState<string>();
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef && inputRef.current) {
      (inputRef.current as any).focus();
    }
  }, [inputRef, entry]);

  useEffect(() => {
    if (apiError) {
      toast.error(apiError);
    }
  }, [apiError]);

  const isIntegratedApp = useAppSelector(getIntegratedAppState).isIntegratedApp;
  const currencySymbol = useAppSelector(getGroupCurrencySymbol);
  const contestantIds = entry.contestant_id
    .split(";")
    .filter((id) => id.length > 0);
  const selectedContestants = contestantIds.map((id) => {
    const contestant = pool.contestants.find((c) => c.id === id);
    return contestant;
  });

  const numberOfSelections = selectedContestants.length;
  const poolTotalKey = ["total_win", "total_exacta", "total_trifecta"][
    numberOfSelections - 1
  ];
  const poolTotal = (pool as any)[poolTotalKey];

  const isBasic = isBasicContest(contest);
  const basicPoolType = (["win", "exacta", "trifecta"] as const)[numberOfSelections - 1];
  const basicPickCost = isBasic && basicPoolType ? picksCostForContestPick(contest, basicPoolType) : 0;
  // Picks remaining is derived from the entry, not wallet balance (which
  // inflates when pool winnings are credited back into the entry).
  const contestEntry = useAppSelector(state => getContestEntryById(state, contestEntryId));
  const remainingPicks = remainingPicksForEntry(contestEntry);
  const basicCanAfford = isBasic ? remainingPicks >= basicPickCost : true;

  const effectiveAmountForCalc = isBasic
    ? basicPickCost
    : formattedCurrencyToNumber(betAmount) || 0;

  const odds = calculateOdds({
    poolTotal,
    entryTotal: entry.bet_total || entry.bets_sum || 0,
    betAmount: effectiveAmountForCalc,
    fee: pool.fee,
  });

  const projectedPayout = effectiveAmountForCalc * (odds || 0);

  const formattedProjectedPayout = formatAsCurrency(projectedPayout);
  const winType = poolTotalKey === "total_win";
  const secondaryColor = useAppSelector(getGroupSecondaryColorOrDefault);

  const hasBets = bets !== undefined;
  const hasContestEntryId = contestEntryId !== undefined;

  const contestMinBetAmount = resolveMinBetAmountForContestEntry(
    bets || [],
    contestEntryId || "",
    numberOfSelections
  );

  const contestMaxBetAmount = resolveMaxBetAmountForContestEntry(
    bets || [],
    contestEntryId || "",
    numberOfSelections
  );

  const showMinBetAmountText =
    hasBets &&
    hasContestEntryId &&
    formattedCurrencyToNumber(betAmount) < contestMinBetAmount &&
    contestMinBetAmount > 0;

  const showMaxBetAmountText =
    hasBets &&
    hasContestEntryId &&
    formattedCurrencyToNumber(betAmount) > contestMaxBetAmount &&
    contestMaxBetAmount < Number.MAX_SAFE_INTEGER;

  useEffect(() => {
    if (showMinBetAmountText) {
      setValidationError(
        `Your picks must be at least ${formatAsCurrency(contestMinBetAmount)}`
      );
    } else if (showMaxBetAmountText) {
      setValidationError(
        `Your picks can be at most ${formatAsCurrency(contestMaxBetAmount)}`
      );
    } else {
      setValidationError(undefined);
    }
  }, [betAmount, showMinBetAmountText, showMaxBetAmountText]);

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        width: "-webkit-fill-available",
        background: "black",
        color: "white",
        padding: 10,
        fontSize: 14,
      }}
    >
      {/* top panel */}
      <div style={{ width: "100%", display: "flex", padding: 2 }}>
        {/* left panel */}
        {!isBasic && (
          <div style={{ flex: 0.5 }}>
            <BlockyBoldText>
              Available Balance:{" "}
              <span style={{ color: secondaryColor }}>{formattedBalance}</span>
            </BlockyBoldText>
            {(validationError || apiError) && (
              <div style={{ marginTop: 5, color: "red", fontSize: 12 }}>
                {apiError || validationError}
              </div>
            )}
          </div>
        )}
        {/* right panel */}
        <div
          style={{
            flex: isBasic ? 1 : 0.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <BlockyBoldText>
              Selected:{" "}
              {!winType &&
                selectedContestants.map((selection, index) => {
                  return (
                    <span key={index} style={{ color: secondaryColor }}>
                      {selection?.name} ({index + 1});{" "}
                    </span>
                  );
                })}
              {winType && (
                <span style={{ color: secondaryColor }}>
                  {entry.contestant_name}
                </span>
              )}
            </BlockyBoldText>
            {isBasic && apiError && (
              <div style={{ marginTop: 5, color: "red", fontSize: 12 }}>
                {apiError}
              </div>
            )}
          </div>
          {isBasic && (
            <BlockyBoldText style={{ whiteSpace: "nowrap", marginLeft: 8 }}>
              Picks Left:{" "}
              <span style={{ color: secondaryColor }}>{remainingPicks}</span>
            </BlockyBoldText>
          )}
        </div>
      </div>

      {/* middle panel */}
      <div style={{ display: "flex", padding: 3 }}>
        {/* left — Pro only */}
        {!isBasic && (
          <div style={{ flex: 0.5 }}>
            <NumberFormat
              id="input-BetModalContent-betAmountInput"
              getInputRef={inputRef}
              onValueChange={(value) => {
                if (!value.floatValue) {
                  setBetAmount("$0.00");
                }

                if (
                  balance !== undefined &&
                  formattedCurrencyToNumber(value.formattedValue) <= balance!
                ) {
                  setBetAmount(value.formattedValue);
                }
              }}
              prefix={currencySymbol}
              decimalScale={2}
              thousandSeparator
              inputMode="decimal"
              fixedDecimalScale
              allowNegative={false}
              autoFocus
              disabled={loading}
              isAllowed={(value) => {
                if (balance === undefined) {
                  return false;
                }
                if (value && value.floatValue) {
                  return value && value?.floatValue <= balance!;
                }
                return true;
              }}
              placeholder="Enter Amount"
              style={{
                borderRadius: 5,
                height: 40,
                color: "black",
                width: "95%",
              }}
            />

            <div style={{ display: "flex" }}>
              <BlockyBoldText style={{ marginTop: 10, display: "flex" }}>
                Odds: &nbsp;
                <ChangeHighlight
                  hideAfter={1000}
                  highlightClassName={styles.highlight}
                >
                  <div
                    ref={createRef()}
                    style={{ color: secondaryColor }}
                  >{`${odds.toFixed(2)}X`}</div>
                </ChangeHighlight>
              </BlockyBoldText>
            </div>
          </div>
        )}
        {/* right — full width on Basic */}
        <div style={{ width: "100%", flex: isBasic ? 1 : 0.5 }}>
          <Button
            style={{
              width: "100%",
              height: 41,
              backgroundColor: secondaryColor,
              color: "black",
            }}
            disabled={
              loading ||
              (isBasic
                ? !basicCanAfford
                : !betAmount ||
                  betAmount === "$0.00" ||
                  (hasBets &&
                    hasContestEntryId &&
                    formattedCurrencyToNumber(betAmount) < contestMinBetAmount &&
                    contestMinBetAmount > 0) ||
                  (hasBets &&
                    hasContestEntryId &&
                    formattedCurrencyToNumber(betAmount) > contestMaxBetAmount &&
                    contestMaxBetAmount < Number.MAX_SAFE_INTEGER))
            }
            onClick={() => {
              // Validate contest entry requirement for shared pools
              if (pool.group_id === NIL_GROUP_ID && !contestEntryId) {
                setApiError("You must join the contest before placing a bet");
                return;
              }

              confirmBet({
                isIntegratedApp,
                betAmount: isBasic ? String(basicPickCost) : betAmount,
                poolEntryId: entry.id || entry.pool_entry_id,
                pool_id: pool.id,
                setLoading,
                groupId: group.id,
                setErrorMessage: setApiError,
                onSuccessfulBetPlaced: (bet: Bet) => onSuccessfulBetPlaced(bet),
                currency: group.currency_code,
                contest_entry_id: contestEntryId,
              });
            }}
          >
            {loading && (
              <Spinner
                animation="border"
                size="sm"
                style={{ marginRight: 10 }}
              />
            )}
            Confirm
          </Button>

          <div style={{ marginTop: 7 }}>
            <div style={{ display: "flex" }}>
              <BlockyBoldText>Expected Return: &nbsp;</BlockyBoldText>
              <BlockyBoldText className={styles.text}>
                <ChangeHighlight
                  hideAfter={1000}
                  highlightClassName={styles.highlight}
                >
                  <span ref={createRef()} style={{ color: secondaryColor }}>
                    {formattedProjectedPayout}
                  </span>
                </ChangeHighlight>
              </BlockyBoldText>
            </div>
          </div>
        </div>
      </div>
      <OpenSansText
        className={styles.disclaimerText}
        style={{ fontSize: 10, textAlign: "center", color: "gray" }}
      >
        By Clicking 'Confirm' the amount indicated will be credited from your
        balance. Expected returns are subject to change based on subsequent
        activity in the pool.
      </OpenSansText>
    </div>
  );
};

export default BetForm;
